import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { useSignIn, useSignUp } from '@clerk/clerk-react';
import { AuthShell, TextInput, PasswordInput } from '@/pages/login';

const signInSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

const signUpSchema = z
  .object({
    email: z.string().email('Enter a valid email'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    message: "Passwords don't match",
    path: ['confirm'],
  });

const codeSchema = z.object({
  code: z.string().min(4, 'Enter the code from your email'),
});

type SignInValues = z.infer<typeof signInSchema>;
type SignUpValues = z.infer<typeof signUpSchema>;
type CodeValues = z.infer<typeof codeSchema>;

function clerkErrorMessage(e: unknown): string {
  const errs = (e as { errors?: { longMessage?: string; message?: string }[] }).errors;
  return errs?.[0]?.longMessage ?? errs?.[0]?.message ?? 'Something went wrong — please try again';
}

/** Hosted accounts portal (password reset lives there), derived from the
 *  publishable key: clerk.<domain> → accounts.<domain>. */
function accountsPortalUrl(): string | null {
  const pk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
  try {
    const host = atob(pk?.split('_')[2] ?? '').replace(/\$$/, '');
    if (host.startsWith('clerk.')) return `https://accounts.${host.slice('clerk.'.length)}`;
    if (host.endsWith('.clerk.accounts.dev')) return `https://${host.replace('.clerk.accounts.dev', '.accounts.dev')}`;
  } catch {
    /* placeholder/malformed key */
  }
  return null;
}

function ErrorNote({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/8 px-3 py-2 text-sm text-destructive">
      {message}
    </div>
  );
}

function SubmitButton({ busy, children }: { busy: boolean; children: React.ReactNode }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-xs transition-opacity hover:opacity-90 disabled:opacity-60"
    >
      {busy && <Loader2 className="size-4 animate-spin" />}
      {children}
    </button>
  );
}

export function ClerkSignInPage() {
  const { isLoaded, signIn, setActive } = useSignIn();
  const [codeStage, setCodeStage] = useState<null | 'first' | 'second'>(null);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<SignInValues>({ resolver: zodResolver(signInSchema) });
  const codeForm = useForm<CodeValues>({ resolver: zodResolver(codeSchema) });
  const portal = accountsPortalUrl();

  const onSubmit = async (values: SignInValues) => {
    if (!isLoaded) return;
    try {
      const res = await signIn.create({ identifier: values.email, password: values.password });
      if (res.status === 'complete') {
        await setActive({ session: res.createdSessionId });
        return;
      }
      // needs_client_trust = correct password on an unrecognized device
      const secondEmail = res.supportedSecondFactors?.find((f) => f.strategy === 'email_code');
      const firstEmail = res.supportedFirstFactors?.find((f) => f.strategy === 'email_code');
      // 'needs_client_trust' is sent at runtime but missing from the legacy
      // SignInResource status union in @clerk/clerk-react 5.61
      const status = res.status as string;
      if ((status === 'needs_client_trust' || status === 'needs_second_factor') && secondEmail) {
        await signIn.prepareSecondFactor({ strategy: 'email_code' });
        setCodeStage('second');
      } else if (res.status === 'needs_first_factor' && firstEmail && 'emailAddressId' in firstEmail) {
        await signIn.prepareFirstFactor({ strategy: 'email_code', emailAddressId: firstEmail.emailAddressId });
        setCodeStage('first');
      } else {
        console.warn('[sign-in] unhandled Clerk status', res.status, res.supportedFirstFactors, res.supportedSecondFactors);
        setError('root', {
          message: `This sign-in needs a verification step the form doesn't support yet (${res.status}). Refresh the page and try again, or contact your administrator.`,
        });
      }
    } catch (e) {
      setError('root', { message: clerkErrorMessage(e) });
    }
  };

  const onVerify = async (values: CodeValues) => {
    if (!isLoaded) return;
    try {
      const res =
        codeStage === 'second'
          ? await signIn.attemptSecondFactor({ strategy: 'email_code', code: values.code })
          : await signIn.attemptFirstFactor({ strategy: 'email_code', code: values.code });
      if (res.status === 'complete') {
        await setActive({ session: res.createdSessionId });
      } else {
        codeForm.setError('root', { message: 'Verification incomplete — check the code and try again.' });
      }
    } catch (e) {
      codeForm.setError('root', { message: clerkErrorMessage(e) });
    }
  };

  if (codeStage) {
    return (
      <AuthShell
        title="Check your email"
        subtitle={`We sent a verification code to ${signIn?.identifier ?? 'your email'}`}
        footer="Didn't get it? Check spam, or go back and sign in again."
      >
        <form key="code" onSubmit={codeForm.handleSubmit(onVerify)} autoComplete="off" className="space-y-4">
          <TextInput
            id="signin-code"
            label="Verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            error={codeForm.formState.errors.code?.message}
            {...codeForm.register('code')}
          />
          <ErrorNote message={codeForm.formState.errors.root?.message} />
          <SubmitButton busy={codeForm.formState.isSubmitting}>Verify & sign in</SubmitButton>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Sign in to your account"
      subtitle="Enter your credentials to continue"
      footer={
        <>
          Don&apos;t have an account?{' '}
          <Link href="/sign-up" className="font-medium text-white/70 underline-offset-2 hover:underline">
            Sign up
          </Link>
        </>
      }
    >
      <form key="credentials" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextInput
          id="clerk-email"
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          error={errors.email?.message}
          {...register('email')}
        />
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label htmlFor="clerk-password" className="text-sm font-medium text-white/80">Password</label>
            {portal && (
              <a
                href={`${portal}/sign-in`}
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                Forgot password?
              </a>
            )}
          </div>
          <PasswordInput id="clerk-password" error={errors.password?.message} {...register('password')} />
        </div>
        <ErrorNote message={errors.root?.message} />
        <SubmitButton busy={isSubmitting || !isLoaded}>Sign in</SubmitButton>
      </form>
    </AuthShell>
  );
}

export function ClerkSignUpPage() {
  const { isLoaded, signUp, setActive } = useSignUp();
  const [verifying, setVerifying] = useState(false);
  const [, navigate] = useLocation();

  const form = useForm<SignUpValues>({ resolver: zodResolver(signUpSchema) });
  const codeForm = useForm<CodeValues>({ resolver: zodResolver(codeSchema) });

  const onSignUp = async (values: SignUpValues) => {
    if (!isLoaded) return;
    try {
      await signUp.create({ emailAddress: values.email, password: values.password });
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
      setVerifying(true);
    } catch (e) {
      form.setError('root', { message: clerkErrorMessage(e) });
    }
  };

  const onVerify = async (values: CodeValues) => {
    if (!isLoaded) return;
    try {
      const res = await signUp.attemptEmailAddressVerification({ code: values.code });
      if (res.status === 'complete') {
        await setActive({ session: res.createdSessionId });
        navigate('/');
      } else {
        codeForm.setError('root', { message: 'Verification incomplete — check the code and try again.' });
      }
    } catch (e) {
      codeForm.setError('root', { message: clerkErrorMessage(e) });
    }
  };

  if (verifying) {
    return (
      <AuthShell
        title="Check your email"
        subtitle={`We sent a verification code to ${signUp?.emailAddress ?? 'your email'}`}
        footer="Didn't get it? Check spam, or go back and try again."
      >
        <form key="code" onSubmit={codeForm.handleSubmit(onVerify)} autoComplete="off" className="space-y-4">
          <TextInput
            id="clerk-code"
            label="Verification code"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            error={codeForm.formState.errors.code?.message}
            {...codeForm.register('code')}
          />
          <ErrorNote message={codeForm.formState.errors.root?.message} />
          <SubmitButton busy={codeForm.formState.isSubmitting}>Verify & continue</SubmitButton>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Create your account"
      subtitle="Access requires an admin invite for your email"
      footer={
        <>
          Already have an account?{' '}
          <Link href="/" className="font-medium text-white/70 underline-offset-2 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form key="credentials" onSubmit={form.handleSubmit(onSignUp)} className="space-y-4">
        <TextInput
          id="signup-email"
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          error={form.formState.errors.email?.message}
          {...form.register('email')}
        />
        <div>
          <label htmlFor="signup-password" className="mb-1.5 block text-sm font-medium text-white/80">Password</label>
          <PasswordInput
            id="signup-password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            error={form.formState.errors.password?.message}
            {...form.register('password')}
          />
        </div>
        <div>
          <label htmlFor="signup-confirm" className="mb-1.5 block text-sm font-medium text-white/80">Confirm password</label>
          <PasswordInput
            id="signup-confirm"
            autoComplete="new-password"
            error={form.formState.errors.confirm?.message}
            {...form.register('confirm')}
          />
        </div>
        {/* Clerk renders its bot-protection widget here on signUp.create */}
        <div id="clerk-captcha" />
        <ErrorNote message={form.formState.errors.root?.message} />
        <SubmitButton busy={form.formState.isSubmitting || !isLoaded}>Create account</SubmitButton>
      </form>
    </AuthShell>
  );
}
